import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Check, X } from "lucide-react";
import { FALSE_BELIEFS, HOST, BG_IMAGES } from "../lib/content";
import { Reveal, scrollToId } from "../lib/anim";

gsap.registerPlugin(ScrollTrigger);

const MANIFESTO_LINES = [
  { text: "GETTING OUT OF DEBT", cls: "text-[#D9E4F5]" },
  { text: "SHOULD NOT TAKE", cls: "text-white/[0.28]" },
  { text: "THIRTY YEARS", cls: "text-[#D9E4F5]" },
  { text: "OF YOUR LIFE.", cls: "text-[#FFFFFF]" },
];

export const Manifesto = () => {
  const ref = useRef(null);
  useEffect(() => {
    const words = ref.current.querySelectorAll(".m-word");
    const tween = gsap.fromTo(
      words,
      { opacity: 0, y: 24 },
      { opacity: 1, y: 0, duration: 0.6, ease: "power2.out", stagger: 0.04,
        scrollTrigger: { trigger: ref.current, start: "top 75%", once: true } }
    );
    return () => { tween.scrollTrigger?.kill(); tween.kill(); };
  }, []);

  return (
    <section className="relative px-6 md:px-12 lg:px-16 py-28 md:py-40 overflow-hidden" data-testid="manifesto-section">
      <div className="absolute inset-y-0 right-0 w-full md:w-[62%] pointer-events-none" data-testid="manifesto-bg-image">
        <img src={BG_IMAGES.homeDusk} alt="" data-parallax="0.1" className="w-full h-full object-cover opacity-35 scale-110" />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, #0E2A54 0%, rgba(9,30,66,0.55) 45%, rgba(9,30,66,0.3) 100%), linear-gradient(180deg, #0E2A54 0%, transparent 28%, transparent 72%, #0E2A54 100%)",
          }}
        />
      </div>
      <div className="relative">
      <div data-parallax="0.45" className="absolute right-8 md:right-16 top-24 grid grid-cols-8 gap-2 opacity-100 pointer-events-none">
        {Array.from({ length: 64 }).map((_, i) => (
          <span key={i} className="w-2.5 h-2.5 bg-[#FFFFFF]/[0.06]" />
        ))}
      </div>
      <p className="font-mono text-[11px] tracking-[0.3em] text-[#FFFFFF] mb-10">[001] — THE PROBLEM</p>
      <h2 ref={ref} className="font-display uppercase tracking-[-0.03em] leading-[0.97] text-[clamp(36px,5.4vw,68px)] max-w-4xl mb-12">
        {MANIFESTO_LINES.map((line) => (
          <span key={line.text} className={`block ${line.cls}`}>
            {line.text.split(" ").map((w, i) => (
              <span key={i} className="m-word inline-block mr-[0.24em]">{w}</span>
            ))}
          </span>
        ))}
      </h2>
      <Reveal>
        <p className="text-[16px] text-white/55 leading-[1.75] max-w-[560px] mb-10">
          Standard debt advice tells you to round up payments, cut lattes, and wait three decades for a payoff date that keeps moving — while the stress compounds right along with the interest. Debt-Free Life takes a different approach: for qualified clients, a dividend-paying whole life policy may redirect money you're already spending to pay debt down sooner, then keep building cash value for retirement and beyond. On a free 1-on-1 call, Najiyyah reviews your situation, checks whether you qualify, and shows you your projected path. Results vary — that's exactly why the conversation is personal.
        </p>
        <button
          data-testid="manifesto-cta-btn"
          data-cta="true"
          onClick={() => scrollToId("#register")}
          className="inline-flex items-center gap-2 border border-[#FFFFFF] text-[#FFFFFF] font-bold text-sm tracking-wide px-8 py-4 hover:bg-[#FFFFFF]/10 transition-colors duration-200"
        >
          <Check className="w-4 h-4" /> BOOK MY FREE CALL
        </button>
      </Reveal>
      </div>
    </section>
  );
};

export const PersonalLetter = () => (
  <section className="relative overflow-hidden isolate bg-white/[0.015] px-6 py-28 md:py-36" data-testid="letter-section">
    <div className="absolute inset-y-0 right-0 w-[42%] -z-10 pointer-events-none" data-testid="letter-bg-image">
      <img src={BG_IMAGES.openDoor} alt="" data-parallax="0.08" className="w-full h-full object-cover opacity-[0.16] scale-110" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, #0E2A54 0%, rgba(9,30,66,0.6) 55%, rgba(9,30,66,0.35) 100%), linear-gradient(180deg, #0E2A54 0%, transparent 30%, transparent 70%, #0E2A54 100%)" }} />
    </div>
    <div className="max-w-[760px] mx-auto">
      <Reveal>
        <p className="font-mono text-[11px] tracking-[0.3em] text-[#FFFFFF] mb-10">[002] — A LETTER FROM NAJIYYAH</p>
        <p className="text-lg text-white/60 italic mb-10">Fellow Homeowner,</p>
        <p className="text-[15px] text-white/55 leading-[1.8] mb-10">
          When I sit down with families carrying a mortgage plus credit cards, student loans, or car notes, I hear the same three beliefs over and over. And every one of them keeps good people stuck for decades:
        </p>
      </Reveal>
      <div className="space-y-4 mb-12">
        {FALSE_BELIEFS.map((belief, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <div className="flex items-start gap-4 p-5 bg-white/[0.05] border border-white/15" data-testid={`false-belief-card-${i + 1}`}>
              <X className="w-5 h-5 text-white/70 shrink-0 mt-0.5" />
              <p className="text-[15px] text-white/70 italic">{belief}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal>
        <div className="w-24 h-px bg-[#FFFFFF] mb-12" />
        <div className="space-y-6 text-[15px] text-white/55 leading-[1.8]">
          <p>
            Here's the truth I share on these calls. First: this strategy is built around the payments you are <em className="text-[#D9E4F5] not-italic font-medium">already making</em> — it's about redirecting existing dollars, not finding new ones. Second: a properly structured dividend-paying whole life policy isn't just a death benefit; its living values can be put to work against your debts while you're very much alive.
          </p>
          <p>
            And third: Debt-Free Life is not a free-for-all product — and that's a good thing. Qualification depends on your health and your financial profile, which keeps the strategy working the way it's designed to. On one focused call, I'll review your situation, tell you honestly whether you qualify, and show you your own projected numbers — so you can judge it for yourself. No hype, no pressure, no guarantees — just a straight answer.
          </p>
        </div>
        <p className="mt-10 font-mono text-sm text-[#FFFFFF]">— Najiyyah Qaasim, {HOST.company}</p>
      </Reveal>
    </div>
  </section>
);
